import { AmountDisplay } from "@/app/shared/ui/amount-display";
import type { DashboardSummary } from "@/types";
import { TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { createElement } from "react";

interface DashboardSummaryProps {
  data?: DashboardSummary;
  isLoading?: boolean;
}

export function DashboardSummaryComponent({ data, isLoading }: DashboardSummaryProps) {
  if (isLoading || !data) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: using index for skeleton loader is fine
            key={i}
            className="rounded-lg border border-border bg-background p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="h-4 w-20 bg-muted rounded animate-pulse" />
              <div className="size-4 bg-muted rounded animate-pulse" />
            </div>
            <div className="h-8 w-24 bg-muted rounded animate-pulse mb-2" />
            <div className="h-3 w-32 bg-muted rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  const netSavingsColor = data.netSavings >= 0 ? "text-success" : "text-destructive";
  const netSavingsIcon = data.netSavings >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">Total Balance</p>
          <Wallet className="size-4 text-muted-foreground/70" />
        </div>
        <div className="text-2xl font-medium text-foreground">
          <AmountDisplay amount={data.totalBalance} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{data.accountCount} accounts</p>
      </div>

      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">Income</p>
          <TrendingUp className="size-4 text-muted-foreground/70" />
        </div>
        <div className="text-2xl font-medium text-success">
          <AmountDisplay amount={data.monthlyIncome} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">This month</p>
      </div>

      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">Expenses</p>
          <TrendingDown className="size-4 text-muted-foreground/70" />
        </div>
        <div className="text-2xl font-medium text-destructive">
          <AmountDisplay amount={data.monthlyExpense} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">This month</p>
      </div>

      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">Net Savings</p>
          {createElement(netSavingsIcon, {
            className: "size-4 text-muted-foreground/70",
          })}
        </div>
        <div className={`text-2xl font-medium ${netSavingsColor}`}>
          <AmountDisplay amount={data.netSavings} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">This month</p>
      </div>
    </div>
  );
}
